/* eslint-disable object-curly-newline */
// this will render a panel (./Panel.jsx) with a metric loading view (./MetricLoadingView.jsx) showing a top level summary (./Summary.jsx), and bottom level data visualization (./charts/Bar.jsx)

import React, {useContext} from 'react';
import {Bar} from './charts/Bar.jsx';
import MetricLoadingView from './MetricLoadingView.jsx';
import Panel from './Panel.jsx';
import PropTypes from 'prop-types';
import {DeltaRelativeInsight, NumericalValue, Summary} from './Summary.jsx';
import {useSetExportConfig} from '../hooks/exportConfigurationHooks.js';
import {useRegisterSlideElementsForPPTExport} from '../hooks/useExport.js';
import {useCurationConfigValue, useComputedCurationConfigValue} from '../hooks/widgetConfiguration.js';
import LandscapeContext from '../contexts/landscapeContext.js';
import retrievePropsByMetricAndInstanceId from './retrievePropsByMetricAndInstanceId.js';

export default function SummaryBar({instanceId, interval = 'day', metric, pdfExportConfig}) {
    const timezone = useCurationConfigValue(instanceId, 'timezone');
    const companyId = useCurationConfigValue(instanceId, 'companyId');
    const dateRanges = useComputedCurationConfigValue(instanceId, 'dateRanges');
    const landscape = useContext(LandscapeContext);

    const timePeriod = useCurationConfigValue(null, 'timePeriod');
    const timePeriodStr = RivalIQ.utils.timePeriod.getTimePeriodDisplayString(timePeriod);

    // we would ideally like to create our panel title from the metric, I think

    const requestMetric = _.omit(metric, ['title', 'description']);

    // we get a partial metric come through in props, and we can build
    // all of the metric requests from that here
    const metricRequests = {
        metric: _.extend({
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.TIKTOK_PRIVATE_PRESENCE_DATA,
            dimensions: ['companyId'],
            measure: metric.measure,
            filters: {
                company: {
                    onlyCompanyIds: [companyId],
                    landscapeId: landscape.id
                },
                timePeriod: {
                    startDate: dateRanges.main.start,
                    endDate: dateRanges.main.end,
                    timezone: timezone
                }
            },
            dimensionReductions: [{
                [interval]: 'sum'
            }]
        }, requestMetric),
        metricDeltaRel: {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.TIKTOK_PRIVATE_PRESENCE_DATA,
            dimensions: ['companyId'],
            measure: {
                field: metric.measure,
                transform: 'deltaRelative',
                transformConfig: {
                    comparePeriodStartDate: dateRanges.compare.start,
                    comparePeriodEndDate: dateRanges.compare.end
                }
            },
            filters: {
                company: {
                    onlyCompanyIds: [companyId],
                    landscapeId: landscape.id
                },
                timePeriod: {
                    startDate: dateRanges.main.start,
                    endDate: dateRanges.main.end,
                    timezone: timezone
                }
            },
            dimensionReductions: [{
                [interval]: 'sum'
            }]
        },
        metricByInterval: _.extend({
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.TIKTOK_PRIVATE_PRESENCE_DATA,
            dimensions: ['companyId', interval],
            measure: metric.measure,
            filters: {
                company: {
                    onlyCompanyIds: [companyId],
                    landscapeId: landscape.id
                },
                timePeriod: {
                    startDate: dateRanges.main.start,
                    endDate: dateRanges.main.end,
                    timezone: timezone
                }
            }
        }, requestMetric)
    };

    // derive the remaining props we need from the metric and instanceId
    const {barProps, metricLoadingViewProps, panelProps, summaryProps} = retrievePropsByMetricAndInstanceId(metric, instanceId, interval);

    useSetExportConfig(instanceId, {
        title: summaryProps.title,
        supportedExportFormats: _.without(_.values(RivalIQ.enums.exportFormat), RivalIQ.enums.exportFormat.CSV),
        supportedExportTypes: _.values(RivalIQ.enums.exportType),
        pdfExportConfig
    });

    useRegisterSlideElementsForPPTExport(instanceId, {
        header: {
            title: summaryProps.title,
            subtitle: timePeriodStr
        }
    });

    return (
        <Panel {...panelProps}>
            <MetricLoadingView {...metricLoadingViewProps}
                metricRequests={metricRequests}>
                {(metricResults) => {

                    const {metric, metricDeltaRel, metricByInterval} = metricResults;

                    barProps.data = metricByInterval.data.map(([, date, value]) => [moment(date).format('MM/DD'), value]);

                    const summaryValue = getNumericMetricData(metricRequests.metric, metric);
                    summaryProps.value = (
                        <NumericalValue
                            formatted={summaryValue.formatted}
                            value={summaryValue.value}
                        />
                    );

                    let insight = null;
                    const deltaRel = getNumericMetricData(metricRequests.metricDeltaRel, metricDeltaRel);
                    if (deltaRel.raw !== null) {
                        insight = (
                            <DeltaRelativeInsight
                                formatted={deltaRel.formatted}
                                value={deltaRel.raw}
                            />
                        );
                    }
                    summaryProps.insight = insight;

                    return <SummaryBarContent barProps={barProps}
                        summaryProps={summaryProps} />;
                }}
            </MetricLoadingView>
        </Panel>
    );
}

SummaryBar.propTypes = {
    instanceId: PropTypes.string,
    interval: PropTypes.string,
    metric: PropTypes.object
};

// exported for storybook
export function SummaryBarContent({barProps, summaryProps}) {
    return (
        <>
            <article className='tlw-px-7.5 tlw-py-5'>
                <Summary {...summaryProps} />
            </article>
            <hr
                className='tlw-my-0 tlw-border-gray-300'
            />
            <article className='tlw-px-7.5 tlw-py-5'>
                {barProps.label}
                <Bar {...barProps} />
            </article>
        </>
    );
}

SummaryBarContent.propTypes = {
    barProps: PropTypes.shape(Bar.propTypes),
    summaryProps: PropTypes.shape(Summary.propTypes)
};

function getNumericMetricData(request, result) {

    return {
        raw: _.last(_.last(result.data)),
        value: _.last(_.last(RivalIQ.metrics.new.MetricFormatter.getFormattedResults(request, result, RivalIQ.metrics.new.enums.MetricFormatTypes.expanded))),
        formatted: _.last(_.last(RivalIQ.metrics.new.MetricFormatter.getFormattedResults(request, result, RivalIQ.metrics.new.enums.MetricFormatTypes.abbreviated)))
    };
}
